import * as React from "react";
import {useMemo, useRef, useState} from "react";
import {ImageModel} from "../../../models/domain/imageModel";
import {useAppContext} from "../../../hooks/useAppContext";
import ReCAPTCHA from "react-google-recaptcha";
import {siteKey, Status} from "../../../definitions/enums";
import {catalogName, identity, validateEmail} from "../../../utils/helpers";
import {webService} from "../../../api/web";
import {Button, FloatingLabel, Form, Modal, Spinner} from "react-bootstrap";
import {BsSend} from "react-icons/bs";

const {email} = identity();

const initialFormState = {
    status: Status.Default,
    from: email ?? '',
    recipient: '',
    note: `Check out this product from ${catalogName}!`
};

export const EmailModal = ({title, sku, image: {url, alt, title: imageTitle}, show, onHide}: { title: string, sku: string, image: ImageModel, show: boolean, onHide: () => void }) => {
    const {productId} = useAppContext();
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [formValues, setFormValues] = useState<{
        status: Status,
        from: string,
        recipient: string,
        note: string
    }>(initialFormState)

    const isFormInvalid = useMemo(() => formValues.from == "" || formValues.recipient === "" || (!validateEmail(formValues.recipient)), [formValues])

    const closeAndReset = () => {
        setFormValues(initialFormState)
        onHide();
    }

    const isRequestPending = formValues.status === Status.Loading;

    const onSubmit = async () => {
        if (isFormInvalid || !recaptchaRef.current || isRequestPending) return;

        // Obtain Recaptcha token before component re-renders!
        const token = await recaptchaRef.current.executeAsync();

        // If no token, set error
        if (!token) {
            setFormValues({...formValues, status: Status.Error})
            return;
        }

        setFormValues({...formValues, status: Status.Loading})

        webService.product
            .share({...formValues, productId, token})
            .then(({status}) => setFormValues({...formValues, status: status === 200 ? Status.Success : Status.Error}))
            .catch(() => setFormValues({...formValues, status: Status.Error}))
            .finally(() => recaptchaRef.current?.reset());
    }

    return (
        <Modal show={show} onHide={closeAndReset}>
            <Modal.Header closeButton>
                <Modal.Title>Share This Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {formValues.status === Status.Success ? (
                    <div className="alert alert-success">
                        <strong>Success!</strong> Your email has been sent to {formValues.recipient} successfully.
                    </div>
                ) : (
                    <>
                        {formValues.status === Status.Error && (
                            <div className="alert alert-danger">
                                <strong>Oops!</strong> Something went wrong, please try again.
                            </div>
                        )}
                        <div className="d-flex">
                            <img src={url}
                                 alt={alt}
                                 title={imageTitle}
                                 width="155"
                                 height="200"/>
                            <div>
                                <strong className="media-heading">{sku} </strong>
                                {title}
                            </div>
                        </div>
                        <hr/>
                        <FloatingLabel controlId="From"
                                       label="Your Name or Email address"
                                       className="mb-3">
                            <Form.Control type="text"
                                          placeholder="Enter your name or email address"
                                          disabled={isRequestPending}
                                          value={formValues.from}
                                          onChange={({target: {value}}) => setFormValues({
                                              ...formValues,
                                              from: value
                                          })}/>
                        </FloatingLabel>
                        <FloatingLabel controlId="To"
                                       label="Recipient's Email address"
                                       className="mb-3">
                            <Form.Control type="email"
                                          autoComplete={"email"}
                                          placeholder="Enter the recipient's email address"
                                          disabled={isRequestPending}
                                          value={formValues.recipient}
                                          onChange={({target: {value}}) => setFormValues({
                                              ...formValues,
                                              recipient: value
                                          })}/>
                        </FloatingLabel>
                        <FloatingLabel controlId="Note"
                                       label="Note to Recipient"
                                       className="mb-3">
                            <Form.Control type="text"
                                          disabled={isRequestPending}
                                          value={formValues.note}
                                          onChange={({target: {value}}) => setFormValues({
                                              ...formValues,
                                              note: value
                                          })}
                            />
                        </FloatingLabel>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="mt-3 mb-0 share-grecaptcha">
                    {formValues.status !== Status.Success && (
                        <small className="text-muted">
                            This site is protected by reCAPTCHA and the Google <a
                            href="https://policies.google.com/privacy" target="blank">Privacy Policy</a> and <a
                            href="https://policies.google.com/terms" target="blank">Terms of Service</a> apply.
                        </small>
                    )}
                    <ReCAPTCHA ref={recaptchaRef}
                               size="invisible"
                               sitekey={siteKey}/>
                </div>
                <Button variant="outline-secondary"
                        onClick={closeAndReset}
                        disabled={isRequestPending}>
                    {formValues.status === Status.Success ? 'Close' : 'Cancel'}
                </Button>
                {(formValues.status === Status.Default || isRequestPending) && (
                    <Button variant="primary"
                            id="share-product"
                            disabled={isFormInvalid || isRequestPending}
                            onClick={onSubmit}>
                        {isRequestPending
                            ? <Spinner animation="border" size="sm" variant="secondary"/>
                            : <><BsSend className="me-1"/> Send Email</>}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}